import React, { useState, FormEvent, useEffect, use } from 'react';
import axios from 'axios';
import { StyleguideComponentProps } from 'lib/component-props';
import { getCancerFormUrl } from 'lib/util';
import { IMaskInput } from 'react-imask';
import Icons from 'components/atoms/Icons';
import { classNames } from 'assets/utils/helper';
import Image from 'next/image';
import { getImaginarysRecaptchaKey } from 'lib/util';
import FormContext from 'components/context/FormContext';
import Select, { StylesConfig, components } from 'react-select';
import { useRouter } from 'next/router';
declare global {
  interface Window {
    grecaptcha: any;
  }
}

type FormFields = {
  csrfmiddlewaretoken: string | null;
  form: string;
  'provider-name': string;
  'provider-npi': string;
  'provider-phone': string;
  'provider-fax': string;
  'provider-office-contact': string;
  'caregiver-name': string;
  'caregiver-dob': string;
  'caregiver-phone': string;
  'caregiver-email': string;
  'patient-name': string;
  'patient-dob': string;
  'patient-phone': string;
  'provider-patient-name': string;
  'provider-patient-dob': string;
  'provider-patient-phone': string;
  'provider-suspicion': string | null;
  'provider-reason': string;
  'patient-services': Array<string>;
  'patient-tests': Array<string>;
  'patient-treatments': Array<string>;
  'provider-tests': Array<string>;
  'provider-treatments': Array<string>;
  'patient-previous': string;
  'patient-goal': string;
  'provider-file-upload': File | null;
  not_a_bot: boolean;
};

type ArrayNames =
  | 'patient-services'
  | 'patient-tests'
  | 'patient-treatments'
  | 'provider-tests'
  | 'provider-treatments';

type SelectOption = {
  value: string;
  label: string;
};

const initialFormState: FormFields = {
  csrfmiddlewaretoken: null,
  form: '',
  'provider-name': '',
  'provider-npi': '',
  'provider-phone': '',
  'provider-fax': '',
  'provider-office-contact': '',
  'caregiver-name': '',
  'caregiver-dob': '',
  'caregiver-phone': '',
  'caregiver-email': '',
  'patient-name': '',
  'patient-dob': '',
  'patient-phone': '',
  'provider-patient-name': '',
  'provider-patient-dob': '',
  'provider-patient-phone': '',
  'provider-suspicion': null,
  'provider-reason': '',
  'patient-services': [],
  'patient-tests': [],
  'patient-treatments': [],
  'provider-tests': [],
  'provider-treatments': [],
  'patient-previous': '',
  'patient-goal': '',
  'provider-file-upload': null,
  not_a_bot: false,
};

const failedMsg =
  'Sorry, something went wrong with the submission. Please try again.';

const CancerForms = (props: StyleguideComponentProps) => {
  const formContext = use(FormContext);
  const [formFields, setFormFields] = useState({
    ...initialFormState,
  });
  const [responseMsg, setResponseMsg] = useState('');
  const [formStage, setFormStage] = useState('intro');
  const [validNPI, setValidNPI] = useState<boolean | null>(null);
  const [validProviderPhone, setValidProviderPhone] = useState<boolean | null>(
    null
  );
  const [validProviderFax, setValidProviderFax] = useState<boolean | null>(
    null
  );
  const [validCaregiverEmail, setValidCaregiverEmail] = useState<
    boolean | null
  >(null);
  const [validCaregiverPhone, setValidCaregiverPhone] = useState<
    boolean | null
  >(null);
  const [validPatientPhone, setValidPatientPhone] = useState<boolean | null>(
    null
  );
  const [validProviderPatientPhone, setValidProviderPatientPhone] = useState<
    boolean | null
  >(null);
  const [validCaregiverBirthdate, setValidCaregiverBirthdate] = useState<
    boolean | null
  >(null);
  const [validPatientBirthdate, setValidPatientBirthdate] = useState<
    boolean | null
  >(null);
  const [validProviderPatientBirthdate, setValidProviderPatientBirthdate] =
    useState<boolean | null>(null);
  const [servicesChecked, setServicesChecked] = useState(false);
  const [patientTestsChecked, setPatientTestsChecked] = useState(false);
  const [patientTreatmentsChecked, setPatientTreatmentsChecked] =
    useState(false);
  const [providerTestsChecked, setProviderTestsChecked] = useState(false);
  const [providerTreatmentsChecked, setProviderTreatmentsChecked] =
    useState(false);
  const [validSelect, setValidSelect] = useState<boolean | null>(null);
  const [formType, setFormType] = useState(
    formContext?.FormState?.formType || ''
  );

  const formUrl = getCancerFormUrl();
  const recaptchaUrl = 'https://www.google.com/recaptcha/api.js';
  const imaginarysKey = getImaginarysRecaptchaKey();

  const router = useRouter();
  const { path, type, ...routerNoPath } = router.query;
  const urlType = type?.toString().toLowerCase();

  const validateDropdown = () => {
    if (
      formFields['provider-suspicion']?.length === 0 ||
      !formFields['provider-suspicion']
    ) {
      setValidSelect(false);
      return;
    }
  };

  //React-Select Config
  const dropdownOptions: Array<SelectOption> = [
    { value: 'Suspicion', label: 'Suspicion' },
    { value: 'Confirmed', label: 'Confirmed' },
  ];

  //Could make these global to reuse
  const selectStyles: StylesConfig<SelectOption, false> = {
    menu: base => ({
      ...base,
      borderRadius: 'var(--br-md) var(--br-md) 0 0',
      boxShadow: '0 4px 11px -2px #999590',
      overflow: 'hidden',
    }),
    menuList: base => ({
      ...base,
      padding: '0',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? 'var(--color-bg-secondary)'
        : 'var(--color-base)',
      cursor: 'pointer',
      fontSize: 'var(--fs-body-copy)',
      lineHeight: 'var(--lh-150)',
      padding: 'calc(var(--spacing-base) * 3) calc(var(--spacing-base) * 5)',
      '&:hover': {
        backgroundColor: 'var(--color-bg-secondary)',
      },
      '&:focus': {
        backgroundColor: 'var(--color-bg-secondary)',
      },
    }),
    control: (base, state) => ({
      ...base,
      border: state.isFocused
        ? '1px solid var(--color-shadow-press)'
        : validSelect === false
          ? '1.5px solid var(--color-errors)'
          : '1.5px solid var(--color-body-dark)',
      borderRadius: 'var(--br-md)',
      boxShadow: 'none',
      '&:hover': {
        borderColor: 'var(--color-primary-dark)',
        borderWidth: '1.5px',
        svg: {
          path: {
            fill: 'var(--color-primary-dark)',
          },
        },
        '.react-select__indicator-separator': {
          backgroundColor: 'var(--color-shadows)',
        },
      },
      svg: {
        path: {
          fill:
            validSelect === false
              ? 'var(--color-body-dark)'
              : 'var(--color-primary-dark)',
        },
      },
      cursor: 'pointer',
      fontSize: 'var(--fs-body-copy)',
      fontFamily: 'var(--ff-sans-serif)',
      fontWeight: 'var(--fw-regular)',
      lineHeight: 'var(--lh-150)',
      opacity: '1',
      padding: 'var(--spacing-base)',
      position: 'relative',
      zIndex: '1',
    }),
    clearIndicator: base => ({
      ...base,
      color: 'var(--color-primary-dark)',
    }),
    indicatorSeparator: base => ({
      ...base,
      backgroundColor:
        validSelect === false
          ? 'var(--color-body-dark)'
          : 'var(--color-shadows)',
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      svg: {
        fill: 'var(--color-primary-dark)',
      },
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : '',
      transition: 'transform 0.5s ease',
    }),
  };

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <Icons id="chevron-down" />
      </components.DropdownIndicator>
    );
  };

  const ClearIndicator = (props: any) => {
    return (
      <components.ClearIndicator {...props}>
        <Icons id="icon-form-close" />
      </components.ClearIndicator>
    );
  };
  //End React-Select COnfig

  const loadRecaptcha = () => {
    const siteHead = document.getElementsByTagName('head')[0];
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = recaptchaUrl;
    siteHead.appendChild(script);
  };

  const updateStage = (stage: string) => {
    setFormStage(stage);
    loadRecaptcha();
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (value.length === 0) {
      e.target
        .closest('.field-wrapper')
        ?.querySelector('.error-msg')
        ?.classList.remove('show');
    }
    setFormFields(prevFields => ({
      ...prevFields,
      [name]: value,
    }));
  };

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;

    setFormFields(prevFields => {
      const updatedOptions = checked
        ? [...prevFields[name as ArrayNames], value]
        : prevFields[name as ArrayNames].filter(option => option !== value);

      switch (name) {
        case 'patient-services':
          updatedOptions.length > 0
            ? setServicesChecked(true)
            : setServicesChecked(false);
          break;
        case 'patient-tests':
          updatedOptions.length > 0
            ? setPatientTestsChecked(true)
            : setPatientTestsChecked(false);
          break;
        case 'patient-treatments':
          updatedOptions.length > 0
            ? setPatientTreatmentsChecked(true)
            : setPatientTreatmentsChecked(false);
          break;
        case 'provider-tests':
          updatedOptions.length > 0
            ? setProviderTestsChecked(true)
            : setProviderTestsChecked(false);
          break;
        case 'provider-treatments':
          updatedOptions.length > 0
            ? setProviderTreatmentsChecked(true)
            : setProviderTreatmentsChecked(false);
          break;
      }

      return {
        ...prevFields,
        [name]: updatedOptions,
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileUpload = e.target.files?.[0];
    fileUpload &&
      setFormFields(prevFields => ({
        ...prevFields,
        ['provider-file-upload']: fileUpload,
      }));
  };

  const validateEmail = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.checkValidity()) {
      setValidCaregiverEmail(true);
      setResponseMsg('');
    } else {
      setValidCaregiverEmail(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const captchaToken = window.grecaptcha.getResponse();

    if (captchaToken) {
      const { ...otherValues } = formFields;
      const formData = {
        ...otherValues,
        form: '510',
      };

      const fd = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (
          !(
            value === undefined ||
            value === null ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)
          )
        ) {
          if (Array.isArray(value)) {
            value.forEach(val => {
              fd.append(key, val);
            });
          } else if (value instanceof File) {
            fd.append(key, value, value.name);
          } else {
            fd.append(key, String(value));
          }
        }
      });

      axios
        .post('/api/sendForm', fd, {
          headers: {
            'X-CSRFToken': `${formFields.csrfmiddlewaretoken}`,
          },
        })
        .then(res => {
          if (res.status >= 200 && res.status < 300) {
            setFormStage('success');
          }
        })
        .catch(function (error) {
          console.error('Submission error:', error);
          setResponseMsg(failedMsg);
        });
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (
      urlType &&
      (urlType === 'patient-request' || urlType === 'provider-request')
    ) {
      setFormType(urlType);
      setFormStage('intro');
    }

    const getMessage = (event: MessageEvent) => {
      if (event.origin !== formUrl.slice(0, formUrl.indexOf('.org') + 4))
        return;

      if (event.data.type === 'csrf-token') {
        const csrfToken = event.data.token;
        setFormFields(prevFields => ({
          ...prevFields,
          csrfmiddlewaretoken: csrfToken,
        }));
      }
    };

    window.addEventListener('message', getMessage);

    return () => window.removeEventListener('message', getMessage);
  }, []);

  useEffect(() => {
    setFormType(urlType || '');
  }, [router.query]);

  useEffect(() => {
    updateStage('intro');
  }, [formContext]);

  return (
    <div className="overhauled" id={`cancer-forms-${props.rendering?.uid}`}>
      <div className="hero lower-level">
        <Image
          src="/images/green-hero-bg.jpg"
          loading="eager"
          alt=""
          fill
          fetchPriority="high"
          className="hero-img"
          sizes="100vw"
        />
        <div className={classNames(`hero-inner-wrapper`)}>
          <div className="jss-inner-container">
            <div className="hero-content">
              <h1
                className={classNames(
                  `hero-title hero-title--left`,
                  'osf-display-h1'
                )}
              >
                {!urlType
                  ? 'Appointment Request Form'
                  : urlType !== 'provider-request'
                    ? formStage.includes('caregiver')
                      ? 'Caregiver New Appointment Request Form'
                      : 'New Patient Appointment Request Form'
                    : 'Provider Referral Form'}
              </h1>
            </div>
          </div>
        </div>
      </div>
      <div className="jss-inner-container">
        <div className="cancer-forms bottom-margin">
          <div className="jss-two-column">
            <div className="left-column">
              {!formType && !type && (
                <div className="intro-page">
                  <h2>Let's get started.</h2>
                  <h3>
                    This referral form will take less than 5 minutes to
                    complete.
                  </h3>
                  <p>
                    You must be at least 18 years old to fill out this form. Any
                    information you give us will be kept secure.
                  </p>
                  <h4>Who is this request for?</h4>
                  <button
                    className="btn-secondary no-icon"
                    onClick={() => {
                      router.push(
                        {
                          pathname: '/appointment-request',
                          query: {
                            ...routerNoPath,
                            type: 'patient-request',
                          },
                        },
                        undefined,
                        { shallow: false }
                      );
                    }}
                  >
                    New Patient
                  </button>
                  <button
                    className="btn-secondary no-icon"
                    onClick={() => {
                      router.push(
                        {
                          pathname: '/appointment-request',

                          query: {
                            ...routerNoPath,
                            type: 'provider-request',
                          },
                        },
                        undefined,
                        { shallow: false }
                      );
                    }}
                  >
                    Physician
                  </button>
                  <div className="bottom-content">
                    <p className="osf-nav-section-titles color-primary-dark">
                      Speak to a Care Advisor
                    </p>
                    <p className="osf-nav-section-titles call-text">
                      <span>Call 1-844-OSF-4-HOPE |</span>
                      <span> 1-844-673-4467</span>
                    </p>
                    <p className="osf-nav-body-copy">
                      Available Monday–Friday from 8 a.m. to 4:30 p.m.
                    </p>
                  </div>
                </div>
              )}
              {formType === 'patient-request' && formStage === 'intro' && (
                <div className="intro-page">
                  <h2>Let's get started.</h2>
                  <h3>
                    This referral form will take less than 5 minutes to
                    complete.
                  </h3>
                  <p>
                    You must be at least 18 years old to fill out this form. Any
                    information you give us will be kept secure.
                  </p>
                  <h4>Who is this request for?</h4>
                  <button
                    className="btn-secondary"
                    onClick={() => updateStage('start')}
                  >
                    Myself
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => updateStage('caregiver-start')}
                  >
                    Someone Else
                  </button>
                </div>
              )}
              {(formStage.includes('start') ||
                (formType === 'provider-request' &&
                  formStage !== 'success')) && (
                <div className="form-wrapper">
                  {formStage.includes('start') && (
                    <button
                      className="btn-secondary back-btn"
                      onClick={() => setFormStage('intro')}
                    >
                      Back to Start
                    </button>
                  )}

                  <p className="osf-label required-note">
                    <span className="required">*</span> Required Field
                  </p>
                  <form onSubmit={handleSubmit} id="cancer-forms">
                    {formType === 'provider-request' && (
                      <>
                        <h3>Provider Information</h3>
                        <fieldset>
                          <div className="field-wrapper">
                            <label htmlFor="providerName">
                              <span className="required">*</span>Provider Name:
                            </label>
                            <input
                              id="providerName"
                              type="text"
                              placeholder="First and Last"
                              name="provider-name"
                              value={formFields['provider-name']}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="field-wrapper">
                            <label htmlFor="providerNPI">
                              <span className="required">*</span>Provider NPI
                              Number:
                            </label>
                            <IMaskInput
                              id="providerNPI"
                              mask="0000000000"
                              value={formFields['provider-npi']}
                              onAccept={value => {
                                setFormFields(prev => ({
                                  ...prev,
                                  ['provider-npi']: value,
                                }));
                              }}
                              placeholder="(e.g., 1234567890)"
                              name="provider-npi"
                              type="text"
                              required
                              onBlur={e => {
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  setValidNPI(null);
                                  return;
                                }
                                const digits = raw.replace(/\D/g, '');
                                setValidNPI(digits.length === 10);
                              }}
                            />
                            {validNPI === false && (
                              <em className="error-msg osf-label">
                                Please provide a valid provider NPI number in
                                the provided format.
                              </em>
                            )}
                          </div>
                          <div className="field-wrapper">
                            <label htmlFor="providerPhone">
                              <span className="required">*</span>Provider Phone
                              Number:
                            </label>
                            <IMaskInput
                              id="providerPhone"
                              mask="(000)000-0000"
                              unmask={true}
                              value={formFields['provider-phone']}
                              onAccept={value => {
                                setFormFields(prev => ({
                                  ...prev,
                                  ['provider-phone']: value,
                                }));
                              }}
                              placeholder="(___)___-____"
                              name="provider-phone"
                              type="text"
                              required
                              onBlur={e => {
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  setValidProviderPhone(null);
                                  return;
                                }
                                const digits = raw.replace(/\D/g, '');
                                setValidProviderPhone(digits.length === 10);
                              }}
                            />
                            {validProviderPhone === false && (
                              <em className="error-msg osf-label">
                                Please provide a valid phone number in the
                                provided format.
                              </em>
                            )}
                          </div>
                          <div className="field-wrapper">
                            <label htmlFor="providerFax">
                              <span className="required">*</span>Provider Fax
                              Number:
                            </label>
                            <IMaskInput
                              id="providerFax"
                              mask="(000)000-0000"
                              unmask={true}
                              value={formFields['provider-fax']}
                              onAccept={value => {
                                setFormFields(prev => ({
                                  ...prev,
                                  ['provider-fax']: value,
                                }));
                              }}
                              placeholder="(___)___-____"
                              name="provider-fax"
                              type="text"
                              required
                              onBlur={e => {
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  setValidProviderFax(null);
                                  return;
                                }
                                const digits = raw.replace(/\D/g, '');
                                setValidProviderFax(digits.length === 10);
                              }}
                            />
                            {validProviderFax === false && (
                              <em className="error-msg osf-label">
                                Please provide a valid fax number in the
                                provided format.
                              </em>
                            )}
                          </div>
                          <div className="field-wrapper">
                            <label htmlFor="officeContact">
                              <span className="required">*</span>Office Contact
                              Person?
                            </label>
                            <input
                              id="officeContact"
                              type="text"
                              name="provider-office-contact"
                              value={formFields['provider-office-contact']}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </fieldset>
                      </>
                    )}
                    {formStage.includes('caregiver') &&
                      formStage.includes('start') && (
                        <>
                          <h3>Caregiver Information</h3>
                          <fieldset>
                            <div className="field-wrapper">
                              <label htmlFor="caregiverName">
                                <span className="required">*</span>Name:
                              </label>
                              <input
                                id="caregiverName"
                                type="text"
                                placeholder="First and Last"
                                name="caregiver-name"
                                value={formFields['caregiver-name']}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            <div className="field-wrapper">
                              <label htmlFor="caregiverBirthdate">
                                <span className="required">*</span>Date of
                                Birth:
                              </label>
                              <IMaskInput
                                id="caregiverBirthdate"
                                mask="00/00/0000"
                                value={formFields['caregiver-dob']}
                                onAccept={value => {
                                  setFormFields(prev => ({
                                    ...prev,
                                    ['caregiver-dob']: value,
                                  }));
                                }}
                                placeholder="DD/MM/YYYY"
                                name="caregiver-dob"
                                type="text"
                                required
                                onBlur={e => {
                                  const raw = e.target.value.trim();
                                  if (!raw) {
                                    setValidCaregiverBirthdate(null);
                                    return;
                                  }
                                  const digits = raw.replace(/\D/g, '');
                                  setValidCaregiverBirthdate(
                                    digits.length === 8
                                  );
                                }}
                              />
                              {validCaregiverBirthdate === false && (
                                <em className="error-msg osf-label">
                                  Please provide a valid birthdate in the
                                  provided format.
                                </em>
                              )}
                            </div>
                            <div className="field-wrapper">
                              <label htmlFor="caregiverPhone">
                                <span className="required">*</span>Phone Number:
                              </label>
                              <IMaskInput
                                id="caregiverPhone"
                                mask="(000)000-0000"
                                unmask={true}
                                value={formFields['caregiver-phone']}
                                onAccept={value => {
                                  setFormFields(prev => ({
                                    ...prev,
                                    ['caregiver-phone']: value,
                                  }));
                                }}
                                placeholder="(___)___-____"
                                name="caregiver-phone"
                                type="text"
                                required
                                onBlur={e => {
                                  const raw = e.target.value.trim();
                                  if (!raw) {
                                    setValidCaregiverPhone(null);
                                    return;
                                  }
                                  const digits = raw.replace(/\D/g, '');
                                  setValidCaregiverPhone(digits.length === 10);
                                }}
                              />
                              {validCaregiverPhone === false && (
                                <em className="error-msg osf-label">
                                  Please provide a valid phone number in the
                                  provided format.
                                </em>
                              )}
                            </div>
                            <div className="field-wrapper">
                              <label htmlFor="caregiverEmail">
                                <span className="required">*</span>Email:
                              </label>
                              <input
                                id="caregiverEmail"
                                type="email"
                                placeholder="name@email.com"
                                name="caregiver-email"
                                value={formFields['caregiver-email']}
                                onBlur={validateEmail}
                                onChange={handleChange}
                                required
                              />
                              {validCaregiverEmail === false && (
                                <em className="error-msg osf-label">
                                  Please provide a valid email.
                                </em>
                              )}
                            </div>
                          </fieldset>
                        </>
                      )}

                    <h3>
                      {formType === 'patient-request'
                        ? 'New Patient Information'
                        : 'Patient Information'}
                    </h3>
                    {formType === 'provider-request' ? (
                      <>
                        <fieldset>
                          <div className="field-wrapper">
                            <label htmlFor="providerPatientName">
                              <span className="required">*</span>
                              Patient Name:
                            </label>
                            <input
                              id="providerPatientName"
                              type="text"
                              placeholder="First and Last"
                              name="provider-patient-name"
                              value={formFields['provider-patient-name']}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="field-wrapper">
                            <label htmlFor="providerPatientBirthdate">
                              <span className="required">*</span>Date of Birth:
                            </label>
                            <IMaskInput
                              id="providerPatientBirthdate"
                              mask="00/00/0000"
                              value={formFields['provider-patient-dob']}
                              onAccept={value => {
                                setFormFields(prev => ({
                                  ...prev,
                                  ['provider-patient-dob']: value,
                                }));
                              }}
                              placeholder="DD/MM/YYYY"
                              name="provider-patient-dob"
                              type="text"
                              required
                              onBlur={e => {
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  setValidProviderPatientBirthdate(null);
                                  return;
                                }
                                const digits = raw.replace(/\D/g, '');
                                setValidProviderPatientBirthdate(
                                  digits.length === 8
                                );
                              }}
                            />
                            {validProviderPatientBirthdate === false && (
                              <em className="error-msg osf-label">
                                Please provide a valid birthdate in the provided
                                format.
                              </em>
                            )}
                          </div>
                          <div className="field-wrapper">
                            <label htmlFor="providerPatientPhone">
                              <span className="required">*</span>Phone Number:
                            </label>
                            <IMaskInput
                              id="providerPatientPhone"
                              mask="(000)000-0000"
                              unmask={true}
                              value={formFields['provider-patient-phone']}
                              onAccept={value => {
                                setFormFields(prev => ({
                                  ...prev,
                                  ['provider-patient-phone']: value,
                                }));
                              }}
                              placeholder="(___)___-____"
                              name="provider-patient-phone"
                              type="text"
                              required
                              onBlur={e => {
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  setValidProviderPatientPhone(null);
                                  return;
                                }
                                const digits = raw.replace(/\D/g, '');
                                setValidProviderPatientPhone(
                                  digits.length === 10
                                );
                              }}
                            />
                            {validProviderPatientPhone == false && (
                              <em className="error-msg osf-label">
                                Please provide a valid phone number in the
                                provided format.
                              </em>
                            )}
                          </div>
                          {formType === 'provider-request' && (
                            <div className="field-wrapper">
                              <label htmlFor="suspicion">
                                <span className="required">*</span>Suspicion of
                                cancer or Confirmed:
                              </label>
                              <div className="dropdown">
                                <Select
                                  components={{
                                    ClearIndicator,
                                    DropdownIndicator,
                                  }}
                                  classNamePrefix="react-select"
                                  isClearable
                                  inputId="suspicion"
                                  name="provider-suspicion"
                                  styles={selectStyles}
                                  placeholder={'- Select -'}
                                  required
                                  options={dropdownOptions}
                                  value={
                                    dropdownOptions.find(
                                      option =>
                                        option.value ===
                                        formFields['provider-suspicion']
                                    ) ?? null
                                  }
                                  onChange={option => {
                                    setFormFields(prevFields => ({
                                      ...prevFields,
                                      ['provider-suspicion']:
                                        option?.value ?? '',
                                    }));
                                    setValidSelect(true);
                                  }}
                                  onBlur={() => setValidSelect(null)}
                                />
                              </div>
                            </div>
                          )}
                        </fieldset>
                      </>
                    ) : (
                      <>
                        <fieldset>
                          <div className="field-wrapper">
                            <label htmlFor="patientName">
                              <span className="required">*</span>
                              Name:
                            </label>
                            <input
                              id="patientName"
                              type="text"
                              placeholder="First and Last"
                              name="patient-name"
                              value={formFields['patient-name']}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="field-wrapper">
                            <label htmlFor="patientBirthdate">
                              <span className="required">*</span>Date of Birth:
                            </label>
                            <IMaskInput
                              id="patientBirthdate"
                              mask="00/00/0000"
                              value={formFields['patient-dob']}
                              onAccept={value => {
                                setFormFields(prev => ({
                                  ...prev,
                                  ['patient-dob']: value,
                                }));
                              }}
                              placeholder="DD/MM/YYYY"
                              name="patient-dob"
                              type="text"
                              required
                              onBlur={e => {
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  setValidPatientBirthdate(null);
                                  return;
                                }
                                const digits = raw.replace(/\D/g, '');
                                setValidPatientBirthdate(digits.length === 8);
                              }}
                            />
                            {validPatientBirthdate === false && (
                              <em className="error-msg osf-label">
                                Please provide a valid birthdate in the provided
                                format.
                              </em>
                            )}
                          </div>
                          <div className="field-wrapper">
                            <label htmlFor="patientPhone">
                              <span className="required">*</span>Phone Number:
                            </label>
                            <IMaskInput
                              id="patientPhone"
                              mask="(000)000-0000"
                              unmask={true}
                              value={formFields['patient-phone']}
                              onAccept={value => {
                                setFormFields(prev => ({
                                  ...prev,
                                  ['patient-phone']: value,
                                }));
                              }}
                              placeholder="(___)___-____"
                              name="patient-phone"
                              type="text"
                              required
                              onBlur={e => {
                                const raw = e.target.value.trim();
                                if (!raw) {
                                  setValidPatientPhone(null);
                                  return;
                                }
                                const digits = raw.replace(/\D/g, '');
                                setValidPatientPhone(digits.length === 10);
                              }}
                            />
                            {validPatientPhone == false && (
                              <em className="error-msg osf-label">
                                Please provide a valid phone number in the
                                provided format.
                              </em>
                            )}
                          </div>
                        </fieldset>
                      </>
                    )}
                    {formType === 'provider-request' && (
                      <fieldset>
                        <label htmlFor="referralReason">
                          Reason for referral
                        </label>
                        <textarea
                          id="referralReason"
                          name="provider-reason"
                          placeholder="Hint text"
                          value={formFields['provider-reason']}
                          onChange={handleChange}
                        ></textarea>
                        <p className="osf-label">
                          Please include any additional testing ordered.
                        </p>
                      </fieldset>
                    )}
                    <div className="checkbox-section">
                      {formType === 'patient-request' && (
                        <fieldset className="checkboxes">
                          <legend>
                            <span className="required">*</span>Which services
                            would you like more information about? (Select all
                            that apply)
                          </legend>
                          <div
                            className={classNames(
                              'check-option',
                              formFields['patient-services'].includes(
                                'Second Opinion Consultations'
                              )
                                ? 'checked'
                                : ''
                            )}
                          >
                            <input
                              type="checkbox"
                              id="consultations"
                              checked={formFields['patient-services'].includes(
                                'Second Opinion Consultations'
                              )}
                              name="patient-services"
                              value="Second Opinion Consultations"
                              onChange={handleCheck}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                              required={!servicesChecked}
                            />
                            <div className="check">
                              <Icons id="icon-checkmark" />
                            </div>
                            <label htmlFor="consultations">
                              Second Opinion Consultations
                            </label>
                          </div>
                          <div
                            className={classNames(
                              'check-option',
                              formFields['patient-services'].includes(
                                'Cancer Support Services & Treatment Services'
                              )
                                ? 'checked'
                                : ''
                            )}
                          >
                            <input
                              type="checkbox"
                              id="treatmentServices"
                              checked={formFields['patient-services'].includes(
                                'Cancer Support Services & Treatment Services'
                              )}
                              name="patient-services"
                              value="Cancer Support Services & Treatment Services"
                              onChange={handleCheck}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                            />{' '}
                            <div className="check">
                              <Icons id="icon-checkmark" />
                            </div>
                            <label htmlFor="treatmentServices">
                              Cancer Support Services & Treatment Services
                            </label>
                          </div>
                          <div
                            className={classNames(
                              'check-option',
                              formFields['patient-services'].includes(
                                'Clinical Trials'
                              )
                                ? 'checked'
                                : ''
                            )}
                          >
                            <input
                              type="checkbox"
                              id="clinicalTrials"
                              checked={formFields['patient-services'].includes(
                                'Clinical Trials'
                              )}
                              name="patient-services"
                              value="Clinical Trials"
                              onChange={handleCheck}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                            />
                            <div className="check">
                              <Icons id="icon-checkmark" />
                            </div>
                            <label htmlFor="clinicalTrials">
                              Clinical Trials
                            </label>
                          </div>
                          <div
                            className={classNames(
                              'check-option',
                              formFields['patient-services'].includes('Other')
                                ? 'checked'
                                : ''
                            )}
                          >
                            <input
                              type="checkbox"
                              id="other"
                              checked={formFields['patient-services'].includes(
                                'Other'
                              )}
                              name="patient-services"
                              value="Other"
                              onChange={handleCheck}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                            />
                            <div className="check">
                              <Icons id="icon-checkmark" />
                            </div>
                            <label htmlFor="other">Other</label>
                          </div>
                        </fieldset>
                      )}
                      {formType === 'provider-request' ? (
                        <>
                          <fieldset className="checkboxes">
                            <legend>
                              <span className="required">*</span>Has the patient
                              had any of the following tests? (Select all that
                              apply)
                            </legend>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-tests'].includes(
                                  'Biopsy/Surgery'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="biopsy"
                                name="provider-tests"
                                value="Biopsy/Surgery"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                                required={!providerTestsChecked}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="biopsy">Biopsy/Surgery</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-tests'].includes(
                                  'Blood Work'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="bloodWork"
                                name="provider-tests"
                                value="Blood Work"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="bloodWork">Blood Work</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-tests'].includes(
                                  'Mammogram/X-Ray'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="mammogram"
                                name="provider-tests"
                                value="Mammogram/X-Ray"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="mammogram">Mammogram/X-Ray</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-tests'].includes(
                                  'CT Scan/MRI/Ultrasound/PET Scan'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="mri"
                                name="provider-tests"
                                value="CT Scan/MRI/Ultrasound/PET Scan"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="mri">
                                CT Scan/MRI/Ultrasound/PET Scan
                              </label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-tests'].includes('None')
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="noTests"
                                name="provider-tests"
                                value="None"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="noTests">None</label>
                            </div>
                          </fieldset>
                          <fieldset className="checkboxes">
                            <legend>
                              <span className="required">*</span>Has your
                              patient had any previous treatments? (Select all
                              that apply)
                            </legend>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-treatments'].includes(
                                  'Chemotherapy'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="chemotherapy"
                                name="provider-treatments"
                                value="Chemotherapy"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                                required={!providerTreatmentsChecked}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="chemotherapy">Chemotherapy</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-treatments'].includes(
                                  'Radiation Therapy'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="radiation"
                                name="provider-treatments"
                                value="Radiation Therapy"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="radiation">
                                Radiation Therapy
                              </label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-treatments'].includes(
                                  'Surgery'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="surgery"
                                name="provider-treatments"
                                value="Surgery"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="surgery">Surgery</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['provider-treatments'].includes(
                                  'None'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="noTreatments"
                                name="provider-treatments"
                                value="None"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="noTreatments">None</label>
                            </div>
                          </fieldset>
                        </>
                      ) : (
                        <>
                          <fieldset className="checkboxes">
                            <legend>
                              <span className="required">*</span>Has the patient
                              had any of the following tests? (Select all that
                              apply)
                            </legend>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-tests'].includes(
                                  'Biopsy/Surgery'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="biopsy"
                                name="patient-tests"
                                value="Biopsy/Surgery"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                                required={!patientTestsChecked}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="biopsy">Biopsy/Surgery</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-tests'].includes(
                                  'Blood Work'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="bloodWork"
                                name="patient-tests"
                                value="Blood Work"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="bloodWork">Blood Work</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-tests'].includes(
                                  'Mammogram/X-Ray'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="mammogram"
                                name="patient-tests"
                                value="Mammogram/X-Ray"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="mammogram">Mammogram/X-Ray</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-tests'].includes(
                                  'CT Scan/MRI/Ultrasound/PET Scan'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="mri"
                                name="patient-tests"
                                value="CT Scan/MRI/Ultrasound/PET Scan"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="mri">
                                CT Scan/MRI/Ultrasound/PET Scan
                              </label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-tests'].includes('None')
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="noTests"
                                name="patient-tests"
                                value="None"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="noTests">None</label>
                            </div>
                          </fieldset>
                          <fieldset className="checkboxes">
                            <legend>
                              <span className="required">*</span>Has the patient
                              had any previous treatments? (Select all that
                              apply)
                            </legend>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-treatments'].includes(
                                  'Chemotherapy'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="chemotherapy"
                                name="patient-treatments"
                                value="Chemotherapy"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                                required={!patientTreatmentsChecked}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="chemotherapy">Chemotherapy</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-treatments'].includes(
                                  'Radiation Therapy'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="radiation"
                                name="patient-treatments"
                                value="Radiation Therapy"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="radiation">
                                Radiation Therapy
                              </label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-treatments'].includes(
                                  'Surgery'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="surgery"
                                name="patient-treatments"
                                value="Surgery"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="surgery">Surgery</label>
                            </div>
                            <div
                              className={classNames(
                                'check-option',
                                formFields['patient-treatments'].includes(
                                  'None'
                                )
                                  ? 'checked'
                                  : ''
                              )}
                            >
                              <input
                                type="checkbox"
                                id="noTreatments"
                                name="patient-treatments"
                                value="None"
                                onChange={handleCheck}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              />
                              <div className="check">
                                <Icons id="icon-checkmark" />
                              </div>
                              <label htmlFor="noTreatments">None</label>
                            </div>
                          </fieldset>
                        </>
                      )}
                    </div>
                    {formType === 'patient-request' && (
                      <>
                        <fieldset>
                          <label htmlFor="testLocations">
                            Please list hospitals, clinics or other offices that
                            performed your tests.
                          </label>
                          <textarea
                            id="testLocations"
                            name="patient-previous"
                            placeholder="Hint text"
                            value={formFields['patient-previous']}
                            onChange={handleChange}
                          ></textarea>
                          <p className="osf-label">
                            Please include the facility name, city and state.
                          </p>
                        </fieldset>
                        <fieldset>
                          <label htmlFor="visitGoal">
                            What is your goal for this visit? (optional)
                          </label>
                          <textarea
                            id="visitGoal"
                            name="patient-goal"
                            placeholder="Hint text"
                            value={formFields['patient-goal']}
                            onChange={handleChange}
                          ></textarea>
                        </fieldset>
                      </>
                    )}
                    {formType === 'provider-request' && (
                      <fieldset>
                        <label htmlFor="signedReferral">
                          Please fax a signed provider-request referral (PDF) to
                          309-717-0458.
                        </label>
                        <div className="drop-area">
                          <div className="inner-upload">
                            <Icons id="icon-upload" />
                            <span>Drop files here or</span>
                            <span className="upload-btn">Click to upload</span>
                          </div>
                          <input
                            id="signedReffereal"
                            type="file"
                            name="provider-file-upload"
                            onChange={handleFileChange}
                          />
                        </div>
                      </fieldset>
                    )}
                    <div
                      className="g-recaptcha"
                      data-sitekey={imaginarysKey}
                    ></div>
                    <fieldset className="verification">
                      <div
                        className={classNames(
                          'check-option',
                          formFields['not_a_bot'] ? 'checked' : ''
                        )}
                      >
                        <input
                          type="checkbox"
                          name="not_a_bot"
                          id="verify"
                          required
                          className="verify"
                          onChange={() =>
                            setFormFields(prevFields => ({
                              ...prevFields,
                              not_a_bot: !prevFields['not_a_bot'],
                            }))
                          }
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                          }}
                          checked={formFields['not_a_bot']}
                        />
                        <div className="check">
                          <Icons id="icon-checkmark" />
                        </div>
                      </div>
                      <label htmlFor="verify">
                        <span className="required">*</span>I verify that the
                        information I have provided is accurate and that I am 18
                        years of age or older.
                      </label>
                    </fieldset>
                    <div className="btns-wrapper">
                      <button
                        type="submit"
                        className="btn-primary submit-btn"
                        onClick={() => validateDropdown()}
                      >
                        Submit
                      </button>
                    </div>
                  </form>
                  <div>
                    <h2 className="response-msg">{responseMsg}</h2>
                  </div>
                </div>
              )}
              {formStage === 'success' && (
                <div className="success-page">
                  <div className="success-header">
                    <Image
                      alt="Success Checkmark GIF"
                      src={'/images/success-gif.gif'}
                      height={64}
                      width={64}
                    />
                    {formType === 'patient-request' ? (
                      <h3>Request Form Submitted </h3>
                    ) : (
                      <h3>Form Submitted</h3>
                    )}
                  </div>
                  <p>
                    Our intake team will contact you to coordinate the next
                    steps. If you have questions, please contact our referral
                    line at 1-844-OSF-4-HOPE, 1-844-673-4467.
                  </p>
                  <p>
                    Our team is available Monday–Friday from 8 a.m. to 4:30 p.m.
                  </p>
                </div>
              )}
            </div>
            <div className="right-column">
              {formType === 'patient-request' && formStage === 'intro' && (
                <>
                  <div className="card">
                    <h3 className="osf-display-h3">Schedule your Mammogram</h3>
                    <p>
                      Schedule your mammogram online in just a few steps. Our
                      simple process makes it easy to get started.
                    </p>
                    <a
                      href="https://www.osfhealthcare.org/services/specialties/breast-health/programs-services/mammogram#schedule"
                      className="btn-tertiary"
                      target="_blank"
                    >
                      Schedule Now
                    </a>
                  </div>
                  <div className="divider"></div>
                  <div className="card bottom-margin">
                    <h3 className="osf-display-h3">Physician Referrals</h3>
                    <p>
                      Referring physicians can submit patient referrals quickly
                      using our secure, online referral form.
                    </p>
                    <a
                      href="/appointment-request?type=provider-request"
                      className="btn-tertiary"
                      target="_blank"
                    >
                      Refer a Patient
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancerForms;
